/*!
// <copyright file="Operations.js" company="Microsoft">
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
*/

/* global util, $format, telemetry, resources, notifications, telemetryLogging, appElements, notificationHandling */

import { util, $format } from "@ax/office-shared-lib";
import { telemetry, TelemetryProperties } from "@ax/office-cdn-app-telemetry";
import { resources } from "@ax/office-cdn-app-resources";
import { notifications } from "./Notifications";
import { telemetryLogging } from "./TelemetryLogging";
import { appElements } from "./Elements";
import { notificationHandling } from "./NotificationHandling";
import * as $ from "jquery";

export namespace operationHandling {

    export interface BeginOperationOptions {

        /** The ID of the source from which the operation originated. */
        SourceID?: string
    }

    export interface UpdateOperationOptions {

        /** The ID of the source from which the operation originated. */
        SourceID?: string
    }

    interface HandlerEndOperationOptions {

        /** The ID of the source from which the operation originated. */
        SourceID?: string;

        /** A value indicating if the operation was canceled. */
        Canceled?: boolean;

        /** Property bag of detailed telemetry data. */
        TelemetryDetails?: any;
    }

    /** Handles status reporting of operations for the app. */
    export class OperationHandler {

        /** The notifications div to interact with. */
        private notificationsDiv: JQuery;

        /** The status text control. */
        private _textElement: JQuery;

        /** The map of operations currently being handled. */
        private _operationMap: util.Map;

        /** The map of operations currently being handled, keyed by the ID of the initiating source. */
        private _sourceOperationsMap: util.Map;

        /** The IDs of controls relating to operation statuses required by the operation status handler. */
        private static ControlIDs = {

            /** ID of the status container element. */
            container: "status",

            /** ID of the status text element. */
            text: "statusText"
        };

        /** The attributes to apply to the status container. */
        private static Attributes = {

            /** The operation active attribute, indicating if there is an operation active or not which determines if the status symbol should be shown. */
            operationActive: "data-operation-active"
        };

        /** The format to use when logging an operation started event. */
        private static OperationStartedEventFormat = "started-{0}-operation";

        /** The format to use when logging an operation ended event. */
        private static OperationCompletedEventFormat = "completed-{0}-operation";

        /**
         * @param notificationsDiv The notifications div to interact with.
         */
        constructor(notificationsDiv: JQuery) {
            /* jshint validthis: true */

            this.notificationsDiv = notificationsDiv;
            this._operationMap = new util.Map();
            this._sourceOperationsMap = new util.Map();
        }

        /** Initializes the operation status handler. */
        public initialize(): void {
            this._textElement = util.findElementById(OperationHandler.ControlIDs.text, this.notificationsDiv);
        }

        /**
         * Cancels any active operations that were initiated from the provided source.
         * @param sourceID The ID of the source that initiated the operations to cancel.
         */
        public cancelOperations(sourceID: string): void {
            var operations = this._sourceOperationsMap.get(sourceID);
            if (operations !== undefined) {
                $.each(operations.getItems(), (index: number, operation: string) => {
                    this.endOperation(operation, "", { SourceID: sourceID, Canceled: true });
                });
            }
        }

        /**
         * Begins an operation using the provided ID to track the operation and the provided text as the status text to display to the user while the operation is taking place.
         * @param operationID The ID to track the operation with.
         * @param text The text to display to the user while the operation is taking place.
         * @param options The optional parameters to adjust how operations are started.
         */
        public beginOperation(operationID: string, text: string, options: BeginOperationOptions = {}): void {

            // The telemetry event options for the end operation event
            const eventIdentifier = operationID;
            const eventOptions: TelemetryProperties = util.telemetry.createInfoTelemetryOptions();

            // If a source ID is provided, add the operation ID to the source operations map
            if (options.SourceID !== undefined) {
                let operations = this._sourceOperationsMap.get(options.SourceID);
                if (operations === undefined) {
                    operations = new util.StringSet();
                    this._sourceOperationsMap.add(options.SourceID, operations);
                }
                operations.add(operationID);
                operationID = telemetryLogging._qualifyBySourceID(operationID, options.SourceID);
                eventOptions.SourceID = options.SourceID;

                // If the current source doesn't already have an operation set ID applied, create and apply a new GUID as the ID
                if (!telemetryLogging.TelemetryOperationSetIdMap.containsKey(options.SourceID)) {
                    telemetryLogging.TelemetryOperationSetIdMap.add(options.SourceID, util.generateGuid());
                }
            }

            this._operationMap.add(operationID, {
                Text: text,
                Time: new Date().getTime()
            });
            this._setText(resources.dynamicsApp.status_BusyFormattedMessage(text));
            util.changeAttribute(this.notificationsDiv, appElements.GeneralAttributes.isShown, true);
            util.changeAttribute(this.notificationsDiv, OperationHandler.Attributes.operationActive, true);

            telemetryLogging.logEvent($format(OperationHandler.OperationStartedEventFormat, eventIdentifier), eventOptions);
        }

        /**
         * Updates the displayed text for the operation with the specified ID.
         * @param operationID The ID of the operation to update.
         * @param text The text to display to the user while the operation is taking place.
         * @param options The optional parameters to adjust how operations are updated.
         */
        public updateOperation(operationID: string, text: string, options?: UpdateOperationOptions): void {

            if (options === undefined) {
                options = {};
            }

            if (options.SourceID !== undefined) {
                operationID = telemetryLogging._qualifyBySourceID(operationID, options.SourceID);
            }

            var operation = this._operationMap.get(operationID);

            // Nothing can be done if the operation is not currently in progress
            if (operation === undefined) {
                return;
            }

            operation.Text = text;
            var keys = this._operationMap.getKeys();

            // Update the displayed text if the requested operation is the currently displayed operation
            if (keys.length > 0 && this._operationMap.get(keys[0]) === operation) {
                this._setText(resources.dynamicsApp.status_BusyFormattedMessage(text));
            }
        }

        /**
         * Ends the operation corresponding to the provided ID and uses the provided text as the status text to display to the user indicating the operation is complete.
         * @param operationID The ID to track the operation with.
         * @param text The text to display to the user indicating the operation is complete.
         * @param options The optional parameters to adjust how operations are ended.
         * @returns The duration of the ended operation in seconds.
         */
        public endOperation(operationID: string, text: string, options: HandlerEndOperationOptions = {}): number {

            // The telemetry event options for the end operation event
            var eventIdentifier = operationID;
            var eventOptions: telemetryLogging.LogEventOptions = $.extend(true, { TelemetryDetails: options.TelemetryDetails === undefined ? {} : options.TelemetryDetails }, util.telemetry.createInfoTelemetryOptions());

            // If a source ID is provided, remove the operation ID to the source operations map
            var sourceActiveOperations = false;
            if (options.SourceID !== undefined) {
                var operations = this._sourceOperationsMap.get(options.SourceID);
                if (operations !== undefined) {
                    operations.remove(operationID);
                    sourceActiveOperations = !operations.isEmpty();
                }
                operationID = telemetryLogging._qualifyBySourceID(operationID, options.SourceID);
                eventOptions.SourceID = options.SourceID;
            }

            // First, get the start time of the operation and calculate its duration in seconds
            var duration;
            var operation = this._operationMap.get(operationID);
            if (operation !== undefined && operation.Time !== undefined) {
                var durationMilliseconds = new Date().getTime() - operation.Time;
                duration = (durationMilliseconds / 1000).toFixed(1);
                eventOptions.TelemetryDetails.Duration = durationMilliseconds;
            } else {
                // If an operation was ended without being started, print a '?' duration
                duration = "?";
            }

            // Remove the operation from the operation map
            this._operationMap.remove(operationID);

            // Get the remaining keys in the operation map
            var keys = this._operationMap.getKeys();

            if (keys.length === 0) {
                // If there are no remaining keys in the map (this was the last operation taking place) display the operation complete text to the user and set it to fade out after a short duration
                util.changeAttribute(this.notificationsDiv, OperationHandler.Attributes.operationActive, false);
                this._setText(text);
                util.changeAttribute(this.notificationsDiv, appElements.GeneralAttributes.isShown, false);
            } else {
                // If there are other operations taking place then find the next most recent operation and adjust the status text to indicate that operation is still taking place
                var lastOperation = this._operationMap.get(keys[0]);
                $.each(keys, (index: number, key: string) => {
                    var operation = this._operationMap.get(key);
                    if (operation.Time > lastOperation.Time) {
                        lastOperation = operation;
                    }
                });
                this._setText(resources.dynamicsApp.status_BusyFormattedMessage(lastOperation.Text));
            }

            telemetryLogging.logEvent($format(OperationHandler.OperationCompletedEventFormat, eventIdentifier), options.Canceled !== true ? eventOptions : undefined);

            // If there are no more active operations, remove the currently applied operation set ID
            if (!sourceActiveOperations) {
                telemetryLogging.TelemetryOperationSetIdMap.remove(options.SourceID);
            }

            // Return the duration with just 1 digit after the decimal point
            return duration;
        }

        /**
         * Sets the status to the provided status, as long as no operations are currently active.
         * @param message The non-operational status message to set.
         */
        public setStatus(message: string): void {

            // If there are no active operations, set the text; otherwise operation messages take priority
            if (this._operationMap.isEmpty()) {

                // Set the status message
                this._setText(message);

                // Adjust attributes to indicate the message is shown, then not shown so the appropriate animation is applied
                util.changeAttribute(this.notificationsDiv, OperationHandler.Attributes.operationActive, false);
                util.changeAttribute(this.notificationsDiv, appElements.GeneralAttributes.isShown, true);
                window.setTimeout(
                    () => {
                        util.changeAttribute(this.notificationsDiv, appElements.GeneralAttributes.isShown, false);
                    },
                    100);
            }
        }

        /**
         * Sets the provided text as the text element's text and title.
         * @param text The text to set.
         */
        public _setText(text): void {
            this._textElement.text(text);
            this._textElement.attr("title", text);
        }
    }

    /** The handler for operation statuses. */
    export let handler: OperationHandler;

    /**
     * Begins an operation which indicates to the client an operation is in progress.
     * @param operationID The ID of the operation which is used to track the same operation from beginning to end so the message can be cleared when the operation completes.
     * @param message The message to display indicating work is taking place for the specified operation.
     * @param options The optional parameters to adjust how operations are started.
     */
    export function beginOperation(operationID: string, message: string, options?: BeginOperationOptions): void {
        operationHandling.handler.beginOperation(operationID, message, options);
    }

    /**
     * Updates the displayed text for the operation with the specified ID.
     * @param operationID The ID of the operation to update.
     * @param message The message to display indicating work is taking place for the specified operation.
     * @param options The optional parameters to adjust how operations are started.
     */
    export function updateOperation(operationID: string, message: string, options?: UpdateOperationOptions): void {
        operationHandling.handler.updateOperation(operationID, message, options);
    }

    export interface EndOperationOptions {

        /** Indicates whether a time stamp should be displayed with the notification; true by default. */
        IncludeTimeStamp?: boolean,

        /** The ID of the source from which the operation originated. */
        SourceID?: string,

        /** Property bag of detailed telemetry data. */
        TelemetryDetails?: any
    }

    /**
     * Ends an operation which indicates to the client an operation has completed and adds a notification to the message center.
     * @param operationID The ID of the operation which is used to track the same operation from beginning to end so the message can be cleared when the operation completes.
     * @param message The message to display indicating work has completed for the specified operation.
     * @param options The optional parameters to adjust how operations are ended.
     */
    export function endOperation(operationID: string, message: string, options?: EndOperationOptions): void {
        options = options || {};
        var duration = operationHandling.handler.endOperation(operationID, message, options);
        message = resources.dynamicsApp.notification_DurationFormat(message, duration);
        notificationHandling.handler.addNotification(new notifications.Notification(notifications.NotificationType.notification, message), { IncludeTimeStamp: options.IncludeTimeStamp });
    }

    /**
     * Cancels any active operations that were initiated from the provided source.
     * @param sourceID The ID of the source that initiated the operations to cancel.
     */
    export function cancelOperations(sourceID: string): void {
        operationHandling.handler.cancelOperations(sourceID);
    }

    /**
     * Sets the status to the provided status, as long as no operations are currently active.
     * @param message The non-operational status message to set.
     */
    export function setStatus(message: string): void {
        if (operationHandling.handler) {
            operationHandling.handler.setStatus(message);
        }
    }
}
